/**
 * OfflineBanner - Banner fixo no topo quando offline ou com pendências
 */

import { useOffline } from '@/contexts/OfflineContext';
import { WifiOff, RefreshCw, X, Cloud, AlertTriangle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SyncStatus } from '@/services/syncService';
import { toast } from 'sonner';
import { useState, useCallback } from 'react';

export default function OfflineBanner() {
  const { 
    isOnline, 
    showOfflineBanner, 
    dismissBanner, 
    syncStatus, 
    localStats,
    triggerSync,
    refreshLocalStats
  } = useOffline();
  const [isSyncing, setIsSyncing] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const handleManualSync = async () => {
    if (!isOnline) {
      toast.error('Sem conexão com a internet');
      return;
    }

    setIsSyncing(true);
    try {
      toast.loading('Sincronizando...', { id: 'banner-sync' });
      const result = await triggerSync();
      
      if (result.success) {
        toast.success('Sincronizado!', { id: 'banner-sync', description: result.message });
      } else {
        toast.error('Erro ao sincronizar', { id: 'banner-sync', description: result.message });
      }
    } catch (error) {
      toast.error('Erro ao sincronizar', { id: 'banner-sync', description: error.message });
    } finally {
      setIsSyncing(false);
      await refreshLocalStats();
    }
  };

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    dismissBanner();
  }, [dismissBanner]);

  // Reset dismissed state when status changes significantly
  const hasPendingItems = localStats.sync_queue_size > 0 || localStats.patients_pending > 0;
  const isSyncingStatus = syncStatus.status === SyncStatus.SYNCING || isSyncing;
  
  // Não mostrar se foi dispensado ou se não há nada para mostrar
  if (dismissed && isOnline && !isSyncingStatus) {
    return null;
  }

  if (isOnline && !isSyncingStatus && !hasPendingItems && !showOfflineBanner) {
    return null;
  }

  // Banner de sincronização em andamento
  if (isSyncingStatus) {
    const progress = syncStatus.total > 0 
      ? Math.round((syncStatus.progress / syncStatus.total) * 100) 
      : 0;
    
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-blue-600 text-white px-4 py-3 shadow-lg print:hidden">
        <div className="container mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <div>
              <p className="font-medium">Sincronizando dados...</p>
              <p className="text-sm text-blue-100">
                {syncStatus.progress} de {syncStatus.total} itens
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-blue-500 rounded-full px-3 py-1 text-sm font-medium">
              {progress}%
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-blue-700"
              onClick={handleDismiss}
              data-testid="close-sync-banner"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Banner offline
  if (!isOnline) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white px-4 py-3 shadow-lg print:hidden">
        <div className="container mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <WifiOff className="w-5 h-5" />
            <div>
              <p className="font-medium">Você está offline</p>
              <p className="text-sm text-amber-100">
                Alterações serão sincronizadas automaticamente quando a conexão for restabelecida.
                {hasPendingItems && (
                  <span className="ml-2 font-semibold">
                    ({localStats.sync_queue_size} pendentes)
                  </span>
                )}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-white hover:bg-amber-600"
            onClick={handleDismiss}
            data-testid="close-offline-banner"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Banner com itens pendentes para sincronizar (online)
  if (isOnline && hasPendingItems) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-4 py-3 shadow-lg print:hidden">
        <div className="container mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Cloud className="w-5 h-5" />
            <div>
              <p className="font-medium">Conexão restabelecida!</p>
              <p className="text-sm text-green-100">
                <span className="font-semibold">{localStats.sync_queue_size}</span> alterações pendentes para sincronizar.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleManualSync}
              className="bg-white text-green-700 hover:bg-green-50 font-medium"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Sincronizar Agora
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-green-700"
              onClick={handleDismiss}
              data-testid="close-pending-banner"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Banner de erro de sincronização
  if (syncStatus.status === SyncStatus.ERROR && syncStatus.errors.length > 0) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white px-4 py-3 shadow-lg print:hidden">
        <div className="container mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5" />
            <div>
              <p className="font-medium">Erro na sincronização</p>
              <p className="text-sm text-red-100">
                {syncStatus.errors[syncStatus.errors.length - 1]}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleManualSync}
              className="bg-white text-red-700 hover:bg-red-50"
              size="sm"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar Novamente
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-red-700"
              onClick={handleDismiss}
              data-testid="close-error-banner"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
